import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { User } from 'firebase/auth';
import { UserProfile } from '@shared/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  user: User | null;
  userProfile: UserProfile | null;
  loading?: boolean;
  requiredRole?: 'admin' | 'superadmin';
}

export function ProtectedRoute({
  children,
  user,
  userProfile,
  loading = false,
  requiredRole
}: ProtectedRouteProps) {
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Handle users without profiles - but be more careful about timing
  if (!userProfile && !location.pathname.includes('/create-profile')) {
    // Check if this is likely a Google user (no profile but authenticated)
    const isGoogleUser = user.providerData.some(provider => provider.providerId === 'google.com');
    
    if (isGoogleUser) {
      // Google user without profile - redirect to create profile
      return <Navigate to="/create-profile" replace />;
    } else {
      // For email/password users, if we don't have a profile yet,
      // it's likely still loading. Show loading instead of error.
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      );
    }
  }

  // If user has a profile but we're on create-profile page, redirect based on role
  if (userProfile && userProfile.role && location.pathname.includes('/create-profile')) {
    // Redirect based on user role
    if (userProfile.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />;
    } else if (userProfile.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // If user has a profile but missing role, treat as incomplete profile
  if (userProfile && !userProfile.role && location.pathname.includes('/create-profile')) {
    // Don't redirect - let them complete the profile
  }

  if (requiredRole && userProfile && userProfile.role !== requiredRole && userProfile.role !== 'superadmin') {
    // User doesn't have required role
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="btn btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

